import { create } from "zustand";

const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000; // 1 hour
const MAX_BUG_REPORTS = 3;
const MAX_FEEDBACK = 5;

type ModalView = "chooser" | "bug" | "feedback" | "success";

type ModalStore = {
  isOpen: boolean;
  view: ModalView;
  successType: "bug" | "feedback";
  setView: (view: ModalView) => void;
  onOpen: (view?: ModalView) => void;
  onClose: () => void;
  handleSuccess: (type: "bug" | "feedback") => void;
  bugTimestamps: number[];
  feedbackTimestamps: number[];
  recordBugSubmission: () => void;
  recordFeedbackSubmission: () => void;
  canSubmitBug: () => boolean;
  canSubmitFeedback: () => boolean;
};

const filterRecent = (timestamps: number[]) =>
  timestamps.filter((t) => Date.now() - t < RATE_LIMIT_WINDOW_MS);

const useFeedbackModal = create<ModalStore>((set, get) => ({
  isOpen: false,
  view: "chooser" as ModalView,
  successType: "feedback" as const,
  setView: (view: ModalView) => set({ view }),
  onOpen: (view?: ModalView) => set({ isOpen: true, view: view ?? "chooser" }),
  onClose: () =>
    set({ isOpen: false, view: "chooser", successType: "feedback" }),
  handleSuccess: (type: "bug" | "feedback") =>
    set({ successType: type, view: "success" }),
  bugTimestamps: [],
  feedbackTimestamps: [],
  recordBugSubmission: () =>
    set((s) => ({
      bugTimestamps: [...filterRecent(s.bugTimestamps), Date.now()],
    })),
  recordFeedbackSubmission: () =>
    set((s) => ({
      feedbackTimestamps: [...filterRecent(s.feedbackTimestamps), Date.now()],
    })),
  canSubmitBug: () =>
    filterRecent(get().bugTimestamps).length < MAX_BUG_REPORTS,
  canSubmitFeedback: () =>
    filterRecent(get().feedbackTimestamps).length < MAX_FEEDBACK,
}));

export default useFeedbackModal;
